import React, { Component, MouseEvent } from "react";
import { Typography } from "@material-ui/core";
import Form, { FormStatus } from "components/forms/Form";
import TextField from "components/forms/TextField";
import GoogleButton from "components/GoogleButton";
import { ValidationGroup, Validators } from "util/validation";
import { register } from "services/auth";
import ProgressSpinner from "components/ProgressSpinner";
import { ValueError } from "util/errors";
import { RegistrationRequest } from "services/entities/auth";
import CheckBoxField from "components/forms/CheckBoxField";
import "./index.scss";

interface RegisterFormState {
  status: FormStatus;
  error?: string;
}

interface RegisterFormProps {
  onRegistered: () => void;
  onLogin?: () => void;
}

export default class RegisterForm extends Component<
  RegisterFormProps,
  RegisterFormState
> {
  private readonly validation: ValidationGroup;
  private readonly model: RegistrationRequest;

  state = {
    status: FormStatus.None,
    error: undefined,
  };

  constructor(props: any) {
    super(props);
    this.model = {
      name: "",
      email: "",
      password: "",
      marketingConsent: true,
    };
    this.validation = new ValidationGroup(this.model);
    this.validation.addField("name", {
      required: Validators.required,
      minLength: Validators.minLength(4),
    });
    this.validation.addField("email", {
      required: Validators.required,
      format: Validators.email,
    });
    this.validation.addField("password", {
      required: Validators.required,
      minLength: Validators.minLength(8),
      minNumbers: Validators.containsNumbers(1),
      minUpperCase: Validators.containsUpperCase(1),
      minLowerCase: Validators.containsLowerCase(1),
    });
  }

  onLoginClick = (ev: MouseEvent) => {
    ev.preventDefault();
    if (this.props.onLogin) {
      this.props.onLogin();
    }
  };

  onSubmit = () => {
    this.setState({ status: FormStatus.Submitting });
    register(this.model).then(
      () => {
        this.setState({ status: FormStatus.Success });
        this.props.onRegistered();
      },
      (err) => {
        if (err instanceof ValueError) {
          for (const key of Object.keys(err.fieldErrors)) {
            for (const fieldError of err.fieldErrors[key]) {
              this.validation.addFieldError(key, fieldError);
            }
          }
          this.setState({
            status: FormStatus.Failure,
            error: err.errors ? err.errors[0] : undefined,
          });
        } else {
          this.setState({
            status: FormStatus.Failure,
            error: "Unknown error.",
          });
        }
      }
    );
  };

  onGoogleButtonLoggedIn = () => {
    this.setState({ status: FormStatus.Success });
    this.props.onRegistered();
  };

  onFieldInput = () => {
    this.setState({ status: FormStatus.None });
  };

  render() {
    const disabled =
      this.state.status === FormStatus.Submitting ||
      this.state.status === FormStatus.Success;
    return (
      <section className="section--actions">
        <Form
          className="register-form"
          onSubmit={this.onSubmit}
          validationGroup={this.validation}
        >
          <Typography variant="h1" component="h2" className="section-title">
            Sign Up to Sook
          </Typography>
          <Typography variant="body1" component="h1" className="text">
            Find, brand and book your space in 5 minutes.
          </Typography>
          <TextField
            name="name"
            label="Name"
            type="text"
            required={true}
            validationGroup={this.validation}
            model={this.model}
            validationMessages={{
              required: "Name is required",
              minLength: "Name must be at least 4 characters",
            }}
            disabled={disabled}
            onInput={this.onFieldInput}
            variant="outlined"
          />
          <TextField
            name="email"
            label="Email"
            type="text"
            required={true}
            validationGroup={this.validation}
            model={this.model}
            validationMessages={{
              required: "Email is required",
              format: "Invalid email",
            }}
            disabled={disabled}
            onInput={this.onFieldInput}
            variant="outlined"
          />
          <TextField
            name="password"
            label="Password"
            type="password"
            className="password"
            required={true}
            validationGroup={this.validation}
            model={this.model}
            validationMessages={{
              required: "Password is required",
              minLength: "Password must be at least 8 characters",
              minNumbers: "Password must contain at least 1 number",
              minUpperCase:
                "Password must contain at least 1 upper case letter",
              minLowerCase:
                "Password must contain at least 1 lower case letter",
            }}
            disabled={disabled}
            onInput={this.onFieldInput}
            variant="outlined"
          />
          <CheckBoxField
            name="marketingConsent"
            label="Please send me news and updates about Sook"
            validationGroup={this.validation}
            model={this.model}
            disabled={disabled}
            onChange={this.onFieldInput}
          />
          <div className="actions">
            <button
              className="submit-button"
              type="submit"
              disabled={disabled}
            >
              {this.state.status === FormStatus.Submitting && (
                <ProgressSpinner />
              )}
              Sign Up
            </button>
            {this.state.status === FormStatus.Failure && (
              <p className="error">{this.state.error}</p>
            )}
            <p className="login">
              Already have an account?{" "}
              <button onClick={this.onLoginClick}>Log in</button>
            </p>
          </div>

          <div className="social-login-container">
            <GoogleButton onLoggedIn={this.onGoogleButtonLoggedIn} />
          </div>
        </Form>
      </section>
    );
  }
}
